<template>

    <v-container fluid
                 :class="mainContainerClasses"
                 :style="'background:'+gradientStyle">

        <cookie-notice :color="settings.cookieNoticeColor"
                       :cookie-notice="subPanelTranslations.cookie_notice"
                       :edit-mode="editMode"
                       :edit-content-only="editContentOnly"
                       :sub-panel-id="subPanelId"
                       :locale="locale"
                       @change:translations="subPanelTranslationsUpdated"
        ></cookie-notice>

        <v-row no-gutters
               :align="fullHeight ? 'stretch' : 'center'"
               justify="center"
               class="main-card">

            <div>
                <!-- MAIN CARD -->
                <v-card light
                        :color="settings.foregroundColor"
                        :class="cardClasses"
                        :elevation="settings.elevation"
                        style="margin-left: auto; margin-right: auto;"
                        :width="viewportWidth"
                        height="100%"
                        max-width="900">

                    <!-- TEST MODE INFO BUTTON -->
                    <v-tooltip v-if="!editMode && componentData.registration && componentData.registration.testData"
                               color="primary" left>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                v-on="on"
                                absolute
                                icon
                                large
                                right
                                color="primary"
                                class="mt-4 test-data-button"
                                @click="testInfoButtonClicked"
                            >
                                <v-icon x-large>mdi-information</v-icon>
                            </v-btn>
                        </template>
                        Show test mode information
                    </v-tooltip>

                    <!-- LOGO -->
                    <div class="text-left">
                        <logo v-if="settings.showLogo"
                              :logo="logo"
                              :size="settings.logoSize"
                              :align="settings.logoAlignment"
                              :edit-mode="editMode"
                              :display-edit-info="editMode || editContentOnly"
                        ></logo>
                    </div>

                    <v-card-text class="pl-0 pr-0 pl-sm-0 pr-sm-0 pl-md-5 pr-md-5 pt-0">

                        <!-- PAGE CONTENTS -->
                        <v-container fluid class="pl-2 pr-2">
                            <v-row dense>

                                <v-col v-show="displayPageContents"
                                       cols="12"
                                       :md="showRewardsEarned ? 8 : 12"
                                       class="subtitle-1 text-left">

                                    <page-contents
                                        :edit-mode="editMode || editContentOnly"
                                        :content="translations.content"
                                        :background-color="settings.foregroundColor"
                                        @change:translations="translationsUpdated"
                                    ></page-contents>
                                </v-col>

                                <!-- REWARDS BOX -->
                                <v-col cols="12" md="4" v-if="showRewardsEarned">
                                    <reward-box :edit-mode="editMode"
                                                :color="settings.rewardBoxColor"
                                                :reward-points="componentData.reward.points"
                                                :rewards-earned-text="subPanelTranslations.rewards_earned_text"
                                                @change:subPanelTranslations="subPanelTranslationsUpdated"
                                    ></reward-box>
                                </v-col>
                            </v-row>
                        </v-container>

                        <!-- UNSUBSCRIBE BUTTON AND FEEDBACK BOX -->
                        <v-scale-transition>
                            <div v-if="showUnsubscribeButton" class="text-center pr-0 pl-0 pl-sm-0 pr-sm-0 pl-md-5 pr-md-5 pt-0">
                                <unsubscribe-widget :edit-mode="editMode"
                                                    @click="unsubscribeClicked=true"
                                                    :settings="settings"
                                                    :sub-panel-id="subPanelId"
                                                    :locale="locale"
                                                    :button-text="subPanelTranslations.confirm_unsubscribe_button_text"
                                                    :comment-box-label="subPanelTranslations.comment_box_label"
                                                    @change:subPanelTranslations="subPanelTranslationsUpdated"
                                                    @change:translations="translationsUpdated"
                                ></unsubscribe-widget>
                            </div>
                        </v-scale-transition>

                        <!-- RESET PASSWORD -->
                        <div v-if="passwordResetWidgetVisible" class="pr-0 pl-0 pl-sm-0 pr-sm-0 pl-md-5 pr-md-5 pt-0">

                            <reset-password-widget :edit-mode="editMode"
                                                   :sub-panel-translations="subPanelTranslations"
                                                   :settings="settings"
                                                   :componentData="componentData.resetPassword"
                                                   @change:subPanelTranslations="subPanelTranslationsUpdated"
                                                   @change:translations="translationsUpdated"
                            ></reset-password-widget>
                        </div>

                        <!-- CREATE PASSWORD -->
                        <div v-if="passwordCreateWidgetVisible" class="pr-0 pl-0 pl-sm-0 pr-sm-0 pl-md-5 pr-md-5 pt-0">

                            <create-password-widget :edit-mode="editMode"
                                                   :sub-panel-translations="subPanelTranslations"
                                                   :settings="settings"
                                                   :componentData="componentData.createPassword"
                                                   @change:subPanelTranslations="subPanelTranslationsUpdated"
                                                   @change:translations="translationsUpdated"
                            ></create-password-widget>
                        </div>

                        <!-- SURVEY RATING -->
                        <div v-if="ratingVisible && !ratingGiven" class="pr-0 pl-0 pl-sm-0 pr-sm-0 pl-md-5 pr-md-5 pt-0">

                            <survey-rating v-model="ratingModel"
                                           :edit-mode="editMode"
                                           :color="settings.surveyBoxColor"
                                           :question-text="subPanelTranslations.rating_question_text"
                                           :thank-you-text="subPanelTranslations.rating_thank_you_text"
                                           @change:subPanelTranslations="subPanelTranslationsUpdated"
                            ></survey-rating>
                        </div>

                        <!-- PROJECT INFO VISIBLE -->
                        <div v-if="projectInfoVisible" class="pr-0 pl-0 pl-sm-0 pr-sm-0 pl-md-5 pr-md-5 pt-0">

                            <project-info :settings="settings"
                                          :edit-mode="editMode"
                                          :sub-panel-translations="subPanelTranslations"
                                          :component-data="componentData.projectInfo"
                                          :display-edit-info="editContentOnly"
                                          @change:subPanelTranslations="subPanelTranslationsUpdated"
                            ></project-info>

                        </div>

                        <!-- REGISTRATION SURVEY -->
                        <div v-if="showRegistrationSurvey" class="pr-0 pl-0 pl-sm-0 pr-sm-0 pl-md-5 pr-md-5 pt-0">

                            <registration-survey :edit-mode="editMode"
                                                 :sub-panel-id="subPanelId"
                                                 :component-data="componentData.registration"
                                                 :locale="locale"
                                                 :settings="settings"
                                                 :sub-panel-translations="subPanelTranslations"
                                                 :show-test-data="showTestInfo"
                                                 :background-color="settings.foregroundColor"
                                                 @started="surveyStarted"
                                                 @test-data-closed="showTestInfo=false"
                                                 @change:subPanelTranslations="subPanelTranslationsUpdated"
                            ></registration-survey>
                        </div>

                        <!-- DYNAMIC PROFILING -->
                        <div v-show="showDynamicProfiling" class="pr-0 pl-0 pl-sm-0 pr-sm-0 pl-md-5 pr-md-5 pt-0">

                            <dynamic-profiling
                                v-if="componentData.dynamicProfiling"
                                :edit-mode="editMode"
                                :settings="dynamicProfilingSettings"
                                :button-color="settings.actionButtonColor"
                                :background-color="settings.surveyBoxColor"
                                :translations="subPanelTranslations"
                                @reward="incrementReward"
                                @completed="dynamicProfilingCompleted"
                                @remaining="updateRemainingQuestionCount"
                            ></dynamic-profiling>

                        </div>

                        <!-- AUTO GROW TO TAKE UP THE REMAINING SPACE ON THE SCREEN -->
                        <div class="flex grow-1">
                        </div>

                        <!--- PROFILE, SUPPORT AND COMMUNITY BUTTONS -->
                        <div class="pl-5 pt-5 pl-sm-0 pr-sm-0 pl-md-5 pr-md-5">

                            <buttons
                                class="flex-grow-1 align-content-end"
                                :edit-mode="editMode"
                                :page-type="type"
                                :support-button-text="subPanelTranslations.support_button_text"
                                :show-support-button="settings.showSupportButton"
                                :community-button-text="subPanelTranslations.community_button_text"
                                :community-button-color="settings.communityButtonColor"
                                :show-community-button="showCommunityButton"
                                :profile-button-text="subPanelTranslations.profile_button_text"
                                :profile-button-color="settings.profileButtonColor"
                                :show-profile-button="showProfileButton"
                                :questions-available="remainingQuestionCount"
                                :background-color="settings.foregroundColor"
                                :community-url="communityUrl"
                                :support-email="supportEmail"
                                :display-edit-info="editContentOnly"
                                @change:subPanelTranslations="subPanelTranslationsUpdated"
                                @profile="dynamicProfilingClicked"
                            ></buttons>
                        </div>

                        <!-- USE NOTICE -->
                        <div class="text-center pr-5 pl-5">
                            <use-notice :edit-mode="editMode"
                                        :use-notice="subPanelTranslations.use_notice"
                                        :sub-panel-id="this.subPanelId"
                                        :locale="this.locale"
                                        :display-edit-info="editContentOnly"
                                        :background-color="settings.foregroundColor"
                                        @change:translations="subPanelTranslationsUpdated"
                            ></use-notice>
                        </div>

                        <json v-if="debug" :data="subPanelTranslations"/>

                    </v-card-text>

                </v-card>

            </div>
        </v-row>

    </v-container>

</template>

<script>

import CookieNotice from "./CookieNotice";
import SurveyRating from "./SurveyRating";
import Logo from "./Logo";
import Buttons from "./Buttons";
import UseNotice from "./UseNotice";
import PageContents from "./PageContents";
import RewardBox from "./RewardBox";
import LandingPageTranslations from "../../../modules/SubPanels/ts/LandingPageTranslations";
import SubPanelTranslations from "../../../modules/SubPanels/ts/SubPanelTranslations";
import LandingPageSettings from "../../../modules/SubPanels/ts/LandingPageSettings";
import UnsubscribeWidget from "./UnsubscribeWidget";
import ResetPasswordWidget from "../../../modules/PanelistResetPassword/components/ResetPasswordWidget";
import DynamicProfiling from "./DynamicProfiling";
import Json from 'vue-json-pretty';
import RegistrationSurvey from "./RegistrationSurvey";
import ProjectInfo from "./ProjectInfo";
import {bootstrap, setOptions} from "vue-gtag";
import CreatePasswordWidget from "../../../modules/PanelistCreatePassword/components/CreatePasswordWidget";

export default {

    components: {
        CreatePasswordWidget,
        Json,
        SurveyRating,
        CookieNotice,
        Logo,
        Buttons,
        UseNotice,
        PageContents,
        RewardBox,
        UnsubscribeWidget,
        ResetPasswordWidget,
        DynamicProfiling,
        RegistrationSurvey,
        ProjectInfo
    },

    props: {

        editMode: {
            type: Boolean,
            required: false,
            default: false,
        },

        editContentOnly: {
            type: Boolean,
            required: false,
            default: false,
        },

        translations: {
            type: LandingPageTranslations,
            required: true,
        },

        subPanelTranslations: {
            type: SubPanelTranslations,
            required: true,
        },

        settings: {
            type: LandingPageSettings,
            required: true,
        },

        type: {
            type: String | null,
            required: true,
        },

        logo: {
            type: String | null,
            required: true,
        },

        communityUrl: {
            type: String | null,
            required: false,
        },

        supportEmail: {
            type: String | null,
            required: false
        },

        subPanelId: {
            type: String | null,
            required: true
        },

        subPanelName: {
            type: String,
            required: true,
        },

        gaEnabled: {
            type: Boolean,
            required: false,
            default: false,
        },

        gaUA: {
            type: String,
            required: false,
            default: null
        },

        locale: {
            type: Object,
            required: true
        },

        rewardsEarned: {
            type: Number | null,
            required: false,
            default: null,
        },

        componentData: {
            type: Object | null,
            required: true,
        },
    },

    data() {
        return {
            debug: false,
            displayRewardsEarned: ['completed', 'profile', 'quota'],
            unsubscribeClicked: false,
            ratingGiven: false,
            rewardPoints: null,
            localeObject: null,
            dynamicProfilingComplete: false,
            remainingQuestionCount: 0,
            displayPageContents: true,
            showTestInfo: false,
            viewportWidth: 900,
            viewportHeight: 900,
            borders: true,
            cardWidth: 900,
            ratingVisibleModel: true,
        }
    },

    mounted() {
        this.rewardPoints = this.rewardsEarned;

        this.handleResize();

        window.addEventListener('resize', () => {
            this.handleResize();
        });

        if (this.editMode) {
            this.remainingQuestionCount = 3;
        }

        this.enableGoogleAnalytics();
    },

    computed: {
        ratingModel: {
            get() {
                if (this.componentData.surveyRating) return this.componentData.surveyRating.rating;
                return null;
            },
            set(rating) {

                this.componentData.surveyRating = rating;

                Bus.$emit('flash-message', {type: 'success', message: this.subPanelTranslations.rating_thank_you_text});

                this.$emit('change:rating', rating);
            }
        },

        cardClasses() {
            let classes = 'd-flex flex-column main-card';

            if (this.editMode) {
                classes += ' mt-15 mb-15';
            }
            return classes;
        },

        projectInfoVisible() {
            return this.type === 'project-entry' && this.componentData.projectInfo.welcome_enabled;
        },

        passwordResetWidgetVisible() {
            return this.type === 'password-reset';
        },

        passwordCreateWidgetVisible() {
            return this.type === 'password-create';
        },

        registrationSurveyVisible() {
            return this.type === 'registration';
        },

        ratingVisible() {

            if (this.type !== 'completed') return false;
            if (!this.componentData.surveyRating) return false;

            return this.componentData.surveyRating.enabled && this.ratingVisibleModel;
        },

        showRewardsEarned() {
            if (this.componentData.reward) return true;
            return false;
        },

        showUnsubscribeButton() {
            return this.type === "unsubscribe";
        },

        showCommunityButton() {
            if (!this.settings.showCommunityButton) return false;
            if (['password-reset','password-create'].includes(this.type)) return false;
            if (this.showDynamicProfiling) return false;
            return true;
        },

        showProfileButton() {
            if (this.remainingQuestionCount === 0) return false;

            // Hide button while profiling takes place
            if (this.showDynamicProfiling) return false;

            return !this.dynamicProfilingComplete;
        },

        showDynamicProfiling() {

            if (this.ratingVisible) return false;
            if (this.dynamicProfilingComplete) return false;
            if (!this.componentData.dynamicProfiling) return false;

            if (['quota', 'profile', 'completed'].includes(this.type)) {
                return this.componentData.dynamicProfiling.enabled && !this.editMode;
            }

            return false;
        },

        dynamicProfilingSettings() {
            if (this.editMode) {
                return {
                    'enabled': true,
                };
            }
            return this.componentData.dynamicProfiling;
        },

        showRegistrationSurvey() {
            return this.type === 'registration' && this.componentData.registration;
        },

        fullHeight() {
            return this.type === 'registration' || this.viewportWidth < 960;
        },

        verticalMargins() {
            if (this.fullHeight && this.viewportHeight > 800 && this.viewportWidth > 960) {
                return true;
            }
            return false;
        },

        mainContainerClasses() {
            let classes = ['main-container'];

            if (this.verticalMargins) {
                classes.push('pt-10 pb-10 pl-0 pr-0');
            } else {
                classes.push('pa-0');
            }

            return classes.join(' ');
        },

        gradientStyle() {
            return "linear-gradient(" + this.settings.gradientAngle + "deg, " + this.settings.gradientStart + "," + this.settings.gradientEnd + ")";
        }
    },

    methods: {

        enableGoogleAnalytics() {

            if (this.gaEnabled) {

                setOptions({
                    config: {id: this.gaUA}
                })

                bootstrap().then(() => {
                    this.$gtag.pageview({
                        page_title: this.subPanelName + ' (' + this.locale.code + ')',
                        page_path: this.$route.path
                    })
                })
            }
        },

        handleResize() {
            this.viewportWidth = window.innerWidth;
            this.viewportHeight = window.innerHeight;
        },

        testInfoButtonClicked() {
            this.showTestInfo = true;
        },

        updateRemainingQuestionCount(remaining) {
            this.remainingQuestionCount = remaining;
        },

        incrementReward(value) {
            this.componentData.reward.points += value;
        },

        subPanelTranslationsUpdated(data) {
            this.$emit('change:subPanelTranslations', Object.assign({}, this.subPanelTranslations, data));
        },

        translationsUpdated(data) {
            this.$emit('change:translations', Object.assign({}, this.translations, data));
        },

        dynamicProfilingClicked() {
            if (!this.editMode) {
                this.dynamicProfilingComplete = false;
                this.ratingVisibleModel = false;
            }
        },

        dynamicProfilingCompleted() {
            this.dynamicProfilingComplete = true;
            this.ratingVisibleModel = true;
        },

        surveyStarted() {
            this.displayPageContents = false;
        },
    },
}
</script>

<style scoped>
.main-container {
    height: 100% !important;
}

.main-card {
    height: 100% !important;
}

.vertical-margins {
    padding-top: 40px;
    padding-bottom: 40px
}

.test-data-button {
    z-index: 100;
    border-radius: 50%;
    background-color: #fff;
    box-shadow: 0 0 1px 1px #ccc;
}

</style>